import { Component, OnInit, OnDestroy } from "@angular/core";
import { AdminService } from "../../app/services/admin.service";
import { TranslateService } from "@ngx-translate/core";
import { LoadingController, ModalController } from "ionic-angular";
import { _i18n_str } from "../../assets/i18n/non-static-ids";
import { AppState } from "../../app/models/app.state";
import { Subscription } from "rxjs/Subscription";
import { FhirService } from "../../app/services/fhir.service";

import * as moment from "moment";
import * as momentBusinessDays from "moment-business-days";
import { switchMap } from "rxjs/operators";
import { groupBy } from "../../app/utils/array-group-by";
import { of } from "rxjs/observable/of";
import { forkJoin } from "rxjs/observable/forkJoin";
import { DashboardService } from "../../app/services/dashboard.service";
import { DiaprofilProTask } from "../../app/models/diaprofil-pro-task";
import { QuestionnaireResponseService } from "@zitelab/ionic-fhir-questionnaires";
import { NavController } from "ionic-angular/navigation/nav-controller";
import { PatientPage } from "../patient.page/patient.page";
import { DiaprofilPatient } from "../../app/models/diaprofil-patient";
import {
  EventLoggingService,
  LogEventId
} from "../../app/services/event-logging.service";
import { ManageTaskModal } from "../../modals/manage-task.modal/manage-task.modal";
import { DashboardPage } from "../dashboard.page/dashboard.page";
import {
  DIAPROFIL_QUESTIONNAIRE_UUID,
  fetchPeriodType
} from "../../app/models/types";
import { flatpickrConfig } from "../../app/models/types";
import { Store } from "@ngrx/store";
import * as UserActions from "../../app/store/actions/user.actions";

@Component({
  selector: "current-consultations-page",
  templateUrl: "current-consultations.page.html"
})
export class CurrentConsultationsPage implements OnInit, OnDestroy {
  userStateSubscription: Subscription;
  state: AppState;
  today = momentBusinessDays(momentBusinessDays().format("YYYY-MM-DD"));
  todayPlusFive = momentBusinessDays()
    .businessAdd(5)
    .format("YYYY-MM-DD");
  todayPlusTen = momentBusinessDays()
    .businessAdd(10)
    .format("YYYY-MM-DD");
  todaySearch = momentBusinessDays().subtract(1, "days"); // Due to time zone issue we use day before today
  intervalStart = momentBusinessDays(this.todaySearch);
  intervalEnd = momentBusinessDays(this.todaySearch);
  tasks: DiaprofilProTask[] = [];
  tasksSubscription: Subscription;
  localizedTableMessages = {};
  fetchPeriod: fetchPeriodType = "TODAY";
  customDateObjStart: Date[] = null;
  customDateObjStop: Date[] = null;
  flatpickrConfig = {
    ...flatpickrConfig,
    minDate: "2000-01-01",
    defaultDate: this.todaySearch.toISOString()
  };
  startFlatPickrConfig = { ...this.flatpickrConfig };
  endFlatPickrConfig = { ...this.flatpickrConfig };
  constructor(
    public admin: AdminService,
    public loadingCtrl: LoadingController,
    private translate: TranslateService,
    private store: Store<AppState>,
    private fhir: FhirService,
    private questionnaireResponseService: QuestionnaireResponseService,
    private dashboardService: DashboardService,
    private navCtrl: NavController,
    private eventLogging: EventLoggingService,
    private modalCtrl: ModalController,
    private dashboard: DashboardService
  ) {
    this.localizedTableMessages = {
      // Message to show when array is presented
      // but contains no values
      emptyMessage: this.translate.instant(
        _i18n_str("CURRENT_CONSULTATIONS_LISTING.MESSAGES.NO_DATA_TO_DISPLAY")
      ),
      // Footer total message
      totalMessage: this.translate.instant(
        _i18n_str("CURRENT_CONSULTATIONS_LISTING.MESSAGES.TOTAL")
      )
    };
  }

  ngOnInit() {
    console.log("ngOninit");
    this.userStateSubscription = this.store
      .select(state => state.user)
      .subscribe(result => {
        this.fetchPeriod = result.currentConsultationsMode;
        if (result.curConsultationsStartDate) {
          this.startFlatPickrConfig.defaultDate =
            result.curConsultationsStartDate;
          this.intervalStart = moment(
            new Date(result.curConsultationsStartDate)
          );
        }
        if (result.curConsultationsEndDate) {
          this.endFlatPickrConfig.defaultDate = result.curConsultationsEndDate;
          this.intervalEnd = moment(new Date(result.curConsultationsEndDate));
        }
      });
  }

  ionViewWillEnter() {
    this.eventLogging.saveEvent({
      event_id: LogEventId.OPEN_CURRENT_CONSULTATIONS,
      event_src_location: "CurrentConsultationsPage"
    });

    this.fetchTasks();
  }

  onCustomFetchPeriodChange(value: any, changedDate: "start" | "stop") {
    if (!value) {
      return;
    }
    const date = moment(value[0]).subtract(1, "days");
    if (changedDate === "start") {
      this.intervalStart = date;
      this.store.dispatch(
        new UserActions.UpdateAction({
          curConsultationsStartDate: date.toISOString()
        })
      );
    } else {
      this.intervalEnd = date;
      this.store.dispatch(
        new UserActions.UpdateAction({
          curConsultationsEndDate: date.toISOString()
        })
      );
    }
    this.fetchTasks();
  }

  onFetchPeriodChange(period: fetchPeriodType) {
    switch (period) {
      case "CUSTOM":
        break;
      case "ALL":
        this.intervalStart = momentBusinessDays(this.todaySearch);
        this.intervalEnd = momentBusinessDays(this.todaySearch).businessAdd(
          9999,
          "days"
        );
        break;
      case "FIVE_WORK_DAYS":
        this.intervalStart = momentBusinessDays(this.todaySearch);
        this.intervalEnd = momentBusinessDays(this.todaySearch).businessAdd(
          5,
          "days"
        );
        break;
      case "TEN_WORK_DAYS":
        this.intervalStart = momentBusinessDays(this.todaySearch);
        this.intervalEnd = momentBusinessDays(this.todaySearch).businessAdd(
          10,
          "days"
        );
        break;
      case "TODAY":
      default:
        this.intervalStart = momentBusinessDays(this.todaySearch);
        this.intervalEnd = momentBusinessDays(this.todaySearch);
        break;
    }
    this.store.dispatch(
      new UserActions.UpdateAction({
        currentConsultationsMode: period
      })
    );
    if (period !== "CUSTOM") {
      this.fetchTasks();
    }
  }

  fetchTasks() {
    let periodString = `period=ge${this.intervalStart.format(
      "YYYY-MM-DD"
    )}&period=le${this.intervalEnd.format("YYYY-MM-DD")}`;
    const loading = this.loadingCtrl.create({
      content: this.translate.instant("GENERIC_UI.GATHERING_TASKS"),
      dismissOnPageChange: true,
      duration: 2000
    });
    loading.present();
    this.tasksSubscription = this.fhir
      .getQuery({
        resourceType: "Task",
        queryParams: {
          code: "|parentTask",
          intent: "DIAPROFIL",
          "part-of:missing": "true",
          status: "ready,in-progress,completed",
          _sort: "-_lastUpdated"
        },
        queryString: periodString
      })
      .pipe(
        switchMap(result => {
          const allTasks: fhir.Task[] =
            result.total > 0
              ? result.entry.map(entry => entry.resource as fhir.Task)
              : [];
          if (allTasks.length === 0) {
            // We return an empty array if no upcoming tasks found
            return of([]);
          }
          /* We cannot in the query itself filter on whether or not taskDate is greater than query period */
          const filteredTasks = allTasks.filter(task => {
            let taskDateStr;
            try {
              taskDateStr = task.restriction.period.end;
            } catch {
              taskDateStr = task.executionPeriod.end;
            }
            let taskDate = moment(taskDateStr).subtract(1, "days");
            return taskDate <= this.intervalEnd;
          });
          const byPatient = groupBy("owner.reference")(filteredTasks);
          if (filteredTasks.length === 0) {
            return of([]);
          }
          return forkJoin(
            Object.keys(byPatient).map(patientFhirId =>
              this.admin.getPatientUser(patientFhirId).pipe(
                switchMap(patientUser => {
                  return this.admin.addPatientHandler(
                    patientUser,
                    byPatient[patientFhirId]
                  );
                })
              )
            )
          );
        })
      )
      .subscribe(result => {
        const allTasks = [];
        result.map(patient => {
          patient.proTasks.map(elem =>
            allTasks.push(
              new DiaprofilProTask(
                elem,
                this.translate,
                this.questionnaireResponseService,
                this.dashboardService,
                patient
              )
            )
          );
        });
        this.tasks = [...allTasks];
        try {
          loading.dismiss().catch();
        } catch {
          console.log("Could not remove loader");
        }
      });
  }

  toPatientPage(patient: DiaprofilPatient) {
    const navParams = {
      patient: patient
    };
    this.navCtrl.push(PatientPage, navParams, { animate: false });
  }

  triggerTaskModal(patient: DiaprofilPatient, task = null) {
    const modal = this.modalCtrl.create(
      ManageTaskModal,
      { patient, task, user: this.admin.userState },
      { cssClass: "medium-modal" }
    );
    modal.present();
    modal.onDidDismiss(() => this.fetchTasks());
  }

  toDashboardPage(task: DiaprofilProTask) {
    if (this.dashboard.widgets === null) {
      const questionnaireResource = this.fhir.resolveReference(
        `Questionnaire/${DIAPROFIL_QUESTIONNAIRE_UUID}`
      ) as fhir.Questionnaire;
      this.dashboard.setGenericProWidgets(questionnaireResource);
    }
    const navParams = {
      prevPageName: "CurrentConsultationsPage",
      responseResource: task.response,
      proTask: task,
      patient: task.patient
    };
    this.navCtrl.push(DashboardPage, navParams, { animate: false });
  }

  ngOnDestroy() {
    try {
      this.tasksSubscription.unsubscribe();
    } catch {}
    try {
      this.userStateSubscription.unsubscribe();
    } catch {}
  }
}



// WEBPACK FOOTER //
// ./src/pages/current-consultations.page/current-consultations.page.ts